import Dinero, { Currency } from 'dinero.js';
import {
  CustomFeeRequest,
  DamageWaiverLineItem,
  DeliveryLineItem,
  DiscountLineItem,
  FeeLineItem,
  FinancialSummary,
  PickupLineItem,
  SecurityDepositLineItem,
  TaxLineItem,
} from '../../../clients/quipli-service/financials/financials';
import { Location } from '../../../clients/quipli-service/location/location';
import { Typography, TypographyWeight } from '../../../components/typography/typography';
import { convertToMoney, safeRound } from '../../../util/util';
import { OrderSection } from '../order-section/order-section';
import { EditableField } from '../../../components/field/field';
import classNames from 'classnames';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { toast } from '../../../util/toasts';
import React, { Dispatch, SetStateAction, isValidElement, useEffect, useState } from 'react';
import { Tooltip } from '../../../components/tooltip/tooltip';
import Icon from '../../../components/icons/icon';
import { TaxCode, TaxBreakdown } from '../../../clients/quipli-service/tax/tax';
import usePrevious from '../../../hooks/use-previous';

type OrderLineItemProps = {
  name: string;
  amount: number;
  formatAmount: string;
  helperText?: React.ReactNode;
  className?: string;
  onChange?: (amount: number) => void;
  onReset?: () => void;
  canReset?: boolean;
  weight?: keyof typeof TypographyWeight;
  isEditable?: boolean;
  isAdditionalEditable?: boolean;
  isAdditionalDisabled?: boolean;
  additionalLabel?: string;
  additionalValue?: any;
  additionalFormattedValue?: string;
  additionalEditableType?: 'text' | 'number' | 'dropdown';
  additionalChildren?: React.ReactNode;
  canResetAdditional?: boolean;
  onAdditionalChange?: (value: any) => void;
  onAdditionalReset?: () => void;
  hasSelect?: any;
  options?: { label: string; value: string }[];
};

export type TaxCodeOptions = {
  taxCodes: TaxCode[];
  setTaxCode: (taxCode?: { id?: string; name?: string; taxCodeSource?: string }) => void;
  taxCode?: { id?: string; name?: string; taxCodeSource?: string };
  defaultTaxCode?: string;
};

export const OrderTotals = ({
  isEditable = false,
  isCycleBilling = false,
  header,
  summary,
  coupon,
  tagPricing,
  setCoupon,
  setCustomDelivery,
  setCustomPickup,
  setCustomTax,
  setCustomTaxPercentage,
  setCustomDamageWaiver,
  setCustomFees,
  setAdditionalDiscount,
  setTaxBreakdown,
  location,
  taxCodeOptions,
  taxBreakdown,
  isDelivery,
  isTaxAmountEditable = true,
  isTaxInclusive = false,
  taxLabel = '',
}: {
  isEditable: boolean;
  isCycleBilling?: boolean;
  header?: React.ReactNode;
  summary?: FinancialSummary;
  coupon: string;
  tagPricing?: string;
  setCoupon: (newCoupon: string) => void;
  setCustomDelivery: (delivery?: Dinero.Dinero) => void;
  setCustomPickup: (pickup?: Dinero.Dinero) => void;
  setCustomTax: (tax?: Dinero.Dinero) => void;
  setCustomTaxPercentage: (percentage?: number) => void;
  setCustomDamageWaiver: (dw?: Dinero.Dinero) => void;
  setCustomFees: Dispatch<SetStateAction<CustomFeeRequest[]>>;
  setAdditionalDiscount: (ad: Dinero.Dinero) => void;
  setTaxBreakdown: (tb?: TaxBreakdown[]) => void;
  location: Location;
  taxCodeOptions?: TaxCodeOptions;
  taxBreakdown?: TaxBreakdown[];
  isDelivery: boolean;
  isTaxAmountEditable?: boolean;
  isTaxInclusive?: boolean;
  taxLabel?: string;
}) => {
  const zero = Dinero({ amount: 0, currency: location.currency as Currency });
  const cartTotal = summary?.lineItems.reduce(
    (acc, line) => (line.type === 'Product' || line.type === 'CustomProduct' ? acc.add(line.amount) : acc),
    Dinero({ amount: 0, currency: location.currency as Currency }),
  );

  //const taxBreakdown = summary?.lineItems.filter((_) => _.type == 'Tax').map((_) => _.taxBreakdown);

  const orderDelivery = summary?.lineItems.find((line) => line.type === 'Delivery') as DeliveryLineItem;
  const delivery = orderDelivery
    ? orderDelivery.amount
    : summary?.lineItems.reduce((acc, line) => (line.type === 'ProductDelivery' ? acc.add(line.amount) : acc), zero) ||
      zero;

  const deliveryTax = taxBreakdown?.reduce((total, jurisdiction) => {
    return total.add(
      jurisdiction.itemizedBreakdown?.find((_) => _.type == 'Delivery' || _.type == 'ProductDelivery')?.tax ?? zero,
    );
  }, zero);
  const pickupTax = taxBreakdown?.reduce((total, jurisdiction) => {
    return total.add(
      jurisdiction.itemizedBreakdown?.find((_) => _.type == 'Pickup' || _.type == 'ProductPickup')?.tax ?? zero,
    );
  }, zero);
  const damageWaiverTax = taxBreakdown?.reduce((total, jurisdiction) => {
    return total.add(jurisdiction.itemizedBreakdown?.find((_) => _.type == 'DamageWaiver')?.tax ?? zero);
  }, zero);
  const additionalDiscountTax = taxBreakdown?.reduce((total, jurisdiction) => {
    return total.add(jurisdiction.itemizedBreakdown?.find((_) => _.type == 'AdditionalDiscount')?.tax ?? zero);
  }, zero);
  const couponTax = taxBreakdown?.reduce((total, jurisdiction) => {
    return total.add(jurisdiction.itemizedBreakdown?.find((_) => _.type == 'Coupon')?.tax ?? zero);
  }, zero);
  const productFeesTax = taxBreakdown?.reduce((total, jurisdiction) => {
    return total.add(jurisdiction.itemizedBreakdown?.find((_) => _.type == 'ProductFee')?.tax ?? zero);
  }, zero);
  const productFeesEATax = taxBreakdown?.reduce((total, jurisdiction) => {
    return total.add(
      jurisdiction.itemizedBreakdown?.find((_) => _.type == 'ProductFee' && _.contractLineItem?.recurring)?.tax ?? zero,
    );
  }, zero);
  const productFeesNotEATax = taxBreakdown?.reduce((total, jurisdiction) => {
    return total.add(
      jurisdiction.itemizedBreakdown?.find((_) => _.type == 'ProductFee' && !_.contractLineItem?.recurring)?.tax ??
        zero,
    );
  }, zero);

  function getTaxFormat(lineItemAmount: Dinero.Dinero, taxAmount?: Dinero.Dinero): string {
    return (
      lineItemAmount.add(isTaxInclusive && taxAmount ? taxAmount : zero).toFormat() +
      `${
        isTaxInclusive
          ? taxAmount && !taxAmount.isZero()
            ? ' (' + taxLabel + ' incl.)'
            : ' (No ' + taxLabel + ')'
          : ''
      }`
    );
  }

  const orderPickup = summary?.lineItems.find((line) => line.type === 'Pickup') as PickupLineItem;
  const pickup = orderPickup
    ? orderPickup.amount
    : summary?.lineItems.reduce(
        (acc, line) => (line.type === 'ProductPickup' ? acc.add(line.amount) : acc),
        Dinero({ amount: 0, currency: location.currency as Currency }),
      ) || Dinero({ amount: 0, currency: location.currency as Currency });

  const damageWaiver = summary?.lineItems.find((line) => line.type === 'DamageWaiver') as DamageWaiverLineItem;
  const damageWaiverTitle = ((): string => {
    const taxableTitle = damageWaiver?.taxable ? ' - (Taxable)' : '';
    if (isCycleBilling) {
      if (damageWaiver?.recurring) {
        return 'Damage Waiver' + taxableTitle + ' - EA';
      } else {
        return '*Damage Waiver' + taxableTitle;
      }
    } else {
      return 'Damage Waiver' + taxableTitle;
    }
  })();

  const discountLineItem = summary?.lineItems.find((line) => line.type === 'TagPricing' || line.type === 'Coupon') as
    | DiscountLineItem
    | undefined;
  const couponTaxFormat = getTaxFormat(discountLineItem?.amount || zero, couponTax);

  const tax = summary?.lineItems.find((line) => line.type === 'Tax') as TaxLineItem;

  setTaxBreakdown(tax?.taxBreakdown);
  const locationFees = summary?.lineItems.filter((line) => line.type === 'Fee') as FeeLineItem[];

  const productFees = summary?.lineItems.reduce(
    (acc, line) => (line.type === 'ProductFee' ? acc.add(line.amount) : acc),
    Dinero({ amount: 0, currency: location.currency as Currency }),
  );

  const productFeesEA = summary?.lineItems.reduce(
    (acc, line) => (line.type === 'ProductFee' && line.recurring ? acc.add(line.amount) : acc),
    Dinero({ amount: 0, currency: location.currency as Currency }),
  );

  const productFeesNotEA = summary?.lineItems.reduce(
    (acc, line) => (line.type === 'ProductFee' && !line.recurring ? acc.add(line.amount) : acc),
    Dinero({ amount: 0, currency: location.currency as Currency }),
  );

  const additionalDiscount =
    summary?.lineItems.find((line) => line.type === 'AdditionalDiscount')?.amount ??
    Dinero({ amount: 0, currency: location.currency as Currency });

  const total = summary?.total.subtract(additionalDiscount).subtract(tax?.amount);

  const orderSecurityDeposit = summary?.lineItems.find((line) => line.type === 'SecurityDeposit') as
    | SecurityDepositLineItem
    | undefined;

  const securityDepositPrice = orderSecurityDeposit
    ? orderSecurityDeposit.amount
    : summary?.lineItems.reduce(
        (acc, line) => (line.type === 'ProductSecurityDeposit' ? acc.add(line.amount) : acc),
        Dinero({ amount: 0, currency: location.currency as Currency }),
      );

  return (
    <OrderSection className="flex flex-col gap-[12px]" header={header}>
      <div className="border-t border-solid border-x-0 border-b-0 border-t-grey-200 pt-[16px] flex flex-col gap-y-[12px] justify-end grow">
        {summary ? (
          <>
            {!isCycleBilling && (
              <OrderLineItem name="Cart Total" amount={cartTotal!.toUnit()} formatAmount={cartTotal!.toFormat()} />
            )}

            {isDelivery && delivery && (
              <OrderLineItem
                name={`${isCycleBilling ? '*' : ''}Delivery ${isCycleBilling ? 'Fees' : ''}`}
                amount={delivery.toUnit()}
                formatAmount={getTaxFormat(delivery, deliveryTax)}
                isEditable={isDelivery && isEditable}
                onChange={(amount) => {
                  setCustomDelivery(Dinero({ amount: safeRound(amount * 100), currency: delivery.getCurrency() }));
                  toast('Delivery updated.');
                }}
                canReset={orderDelivery?.hasCustomAmount || false}
                onReset={() => {
                  setCustomDelivery(undefined);
                  toast('Delivery updated to default.');
                }}
              />
            )}

            {isDelivery && pickup && (
              <OrderLineItem
                name={`${isCycleBilling ? '*' : ''}Pickup ${isCycleBilling ? 'Fees' : ''}`}
                amount={pickup.toUnit()}
                formatAmount={getTaxFormat(pickup, pickupTax)}
                isEditable={isDelivery && isEditable}
                onChange={(amount) => {
                  setCustomPickup(Dinero({ amount: safeRound(amount * 100), currency: pickup.getCurrency() }));
                  toast('Pickup updated.');
                }}
                canReset={orderPickup?.hasCustomAmount || false}
                onReset={() => {
                  setCustomPickup(undefined);
                  toast('Pickup updated to default.');
                }}
              />
            )}

            <OrderCoupon
              tagPricing={tagPricing}
              coupon={coupon}
              discountLineItem={discountLineItem}
              onChange={setCoupon}
              isEditable={isEditable}
              taxFormat={couponTaxFormat}
            />

            {damageWaiver && (
              <OrderLineItem
                name={damageWaiverTitle}
                amount={damageWaiver.amount.toUnit()}
                formatAmount={getTaxFormat(damageWaiver.amount, damageWaiverTax)}
                isEditable={isEditable}
                onChange={(amount) => {
                  setCustomDamageWaiver(
                    Dinero({ amount: safeRound(amount * 100), currency: damageWaiver.amount.getCurrency() }),
                  );
                  toast('Damage Waiver updated.');
                }}
                canReset={damageWaiver.hasCustomAmount}
                onReset={() => {
                  setCustomDamageWaiver(undefined);
                  toast('Damage Waiver updated to default.');
                }}
              />
            )}

            {locationFees?.map((fee) => {
              const locationFeeTitle = ((): string => {
                const taxableTitle = fee.taxable ? ' - (Taxable)' : '';
                if (isCycleBilling) {
                  if (fee?.recurring) {
                    return `${fee.description}` + taxableTitle + ' - EA';
                  } else {
                    return `*${fee.description}` + taxableTitle;
                  }
                } else {
                  return `${fee.description}` + taxableTitle;
                }
              })();

              return (
                <OrderLineItem
                  key={`fee-${fee.feeId}-line-item`}
                  name={locationFeeTitle}
                  amount={fee.amount.toUnit()}
                  formatAmount={getTaxFormat(
                    fee.amount,
                    taxBreakdown?.reduce((tax, breakdown) => {
                      return tax.add(
                        breakdown.itemizedBreakdown?.find(
                          (b) => b.type === 'Fee' && (b.contractLineItem as FeeLineItem)?.feeId === fee.feeId,
                        )?.tax ?? Dinero({ amount: 0, currency: location.currency as Currency }),
                      );
                    }, Dinero({ amount: 0, currency: location.currency as Currency })),
                  )}
                  isEditable={isEditable}
                  onChange={(amount) => {
                    const newFee: CustomFeeRequest = {
                      feeId: fee.feeId,
                      amount: safeRound(amount * 100),
                    };
                    setCustomFees((prev) => [...prev.filter((custom) => custom.feeId !== fee.feeId), newFee]);
                    toast(`${fee.description} updated.`);
                  }}
                  canReset={fee.hasCustomAmount}
                  onReset={() => {
                    setCustomFees((prev) => prev.filter((custom) => custom.feeId !== fee.feeId));
                    toast(`${fee.description} updated to default.`);
                  }}
                />
              );
            })}

            {isCycleBilling ? (
              <>
                {productFeesEA && (
                  <OrderLineItem
                    name={`Product Fees - EA`}
                    amount={productFeesEA.toUnit()}
                    formatAmount={getTaxFormat(productFeesEA, productFeesEATax)}
                  />
                )}

                {productFeesNotEA && (
                  <OrderLineItem
                    name={`*Product Fees`}
                    amount={productFeesNotEA.toUnit()}
                    formatAmount={getTaxFormat(productFeesNotEA, productFeesNotEATax)}
                  />
                )}
              </>
            ) : (
              productFees && (
                <OrderLineItem
                  name={`Product Fees `}
                  amount={productFees.toUnit()}
                  formatAmount={getTaxFormat(productFees, productFeesTax)}
                />
              )
            )}

            {!isCycleBilling && (
              <OrderLineItem
                name="Order Total"
                amount={total!.toUnit()}
                formatAmount={total!.toFormat()}
                weight="medium"
              />
            )}

            <OrderLineItem
              name="Additional Discount"
              amount={additionalDiscount.toUnit()}
              formatAmount={getTaxFormat(additionalDiscount, additionalDiscountTax)}
              onChange={(amount) => {
                setAdditionalDiscount(
                  Dinero({ amount: safeRound(amount * 100), currency: additionalDiscount.getCurrency() }),
                );
                toast('Additional Discount updated.');
              }}
              isEditable={isEditable}
              canReset={additionalDiscount.getAmount() != 0}
              onReset={() => {
                setAdditionalDiscount(additionalDiscount.subtract(additionalDiscount));
                toast('Additional Discount updated.');
              }}
            />
            {tax && (
              <OrderTax
                tax={tax}
                setCustomTax={setCustomTax}
                setCustomTaxPercentage={setCustomTaxPercentage}
                isAmountEditable={isTaxAmountEditable && isEditable}
                isPercentageEditable={isEditable}
                isCycleBilling={isCycleBilling}
                taxCodeOptions={taxCodeOptions}
                isDelivery={isDelivery}
                taxBreakdown={taxBreakdown}
              />
            )}
            {!isCycleBilling && (
              <OrderLineItem
                name="Total"
                amount={summary.total.toUnit()}
                formatAmount={summary.total.toFormat()}
                weight="medium"
              />
            )}

            {(securityDepositPrice?.getAmount() || 0) > 0 && (
              <OrderLineItem
                className="underline"
                name={`Refundable Security Deposit (Hold)${
                  orderSecurityDeposit?.percentage ? ` ${orderSecurityDeposit.percentage}%` : ''
                }`}
                amount={securityDepositPrice!.toUnit()}
                formatAmount={securityDepositPrice!.toFormat()}
              />
            )}
          </>
        ) : (
          <OrderDefaultTotals location={location} />
        )}
      </div>
    </OrderSection>
  );
};

const OrderLineItem = ({
  name,
  helperText,
  weight = 'regular',

  amount,
  formatAmount,
  className,
  canReset = false,
  isEditable = false,
  onChange,
  onReset,

  additionalLabel = '',
  additionalValue = '',
  additionalFormattedValue = '',
  additionalEditableType = 'number',
  additionalChildren,
  canResetAdditional = false,
  isAdditionalEditable = false,
  isAdditionalDisabled = false,
  onAdditionalChange,
  onAdditionalReset,
  options,
}: OrderLineItemProps) => {
  return (
    <div className="flex justify-between items-center w-full">
      <div className="basis-1/2">
        <div className="flex items-center gap-[4px]">
          <Typography
            type="text-sm"
            weight={weight}
            className={classNames(className, {
              'text-grey-800': amount > 0,
              'text-grey-400': amount === 0,
            })}
          >
            {name}
          </Typography>

          {isAdditionalEditable && (
            <div>
              <EditableField
                type={additionalEditableType}
                label={additionalLabel}
                value={additionalValue}
                formattedValue={additionalFormattedValue}
                onChange={(newAmount) => onAdditionalChange && onAdditionalChange(newAmount)}
                canReset={canResetAdditional}
                resetPosition="right"
                onReset={onAdditionalReset}
                disabled={isAdditionalDisabled}
                options={options}
              />
            </div>
          )}

          {isValidElement(helperText) && helperText}
        </div>

        {isAdditionalEditable && additionalChildren}
      </div>

      <div className="basis-1/2 flex justify-end">
        {isEditable ? (
          <div>
            <EditableField
              type="number"
              label={name}
              value={amount}
              formattedValue={formatAmount}
              onChange={(newAmount) => onChange && onChange(newAmount || 0)}
              canReset={canReset}
              onReset={onReset}
            />
          </div>
        ) : (
          <Typography
            type="text-sm"
            weight={weight}
            className={classNames({
              'text-grey-800': amount > 0,
              'text-grey-400': amount === 0,
            })}
          >
            {formatAmount}
          </Typography>
        )}
      </div>
    </div>
  );
};

const OrderCoupon = ({
  isEditable = false,
  coupon,
  taxFormat,
  tagPricing,
  discountLineItem,
  onChange,
}: {
  isEditable?: boolean;
  coupon: string;
  taxFormat: string;
  tagPricing?: string;
  discountLineItem?: DiscountLineItem;
  onChange?: (newCoupon: string) => void;
}) => {
  const { getCouponByCode } = useAPI();

  const handleOnChange = async (newCoupon: string) => {
    if (newCoupon === '-NA-' || newCoupon === '') {
      onChange && onChange('-NA-');
      toast('Coupon removed.');
      return;
    }

    try {
      const response = await getCouponByCode(newCoupon);
      if (response) {
        onChange && onChange(response.code);
        toast('Coupon applied.');
      }
    } catch (error) {
      toast('Invalid coupon code.', 'error');
    }
  };

  return (
    <OrderLineItem
      name="Coupon"
      additionalLabel="Code"
      additionalValue={coupon}
      additionalFormattedValue={coupon === tagPricing ? `[Tag{${tagPricing}}]` : `[${coupon}]`}
      additionalChildren={
        tagPricing &&
        coupon === '-NA-' && (
          <div
            className="cursor-pointer"
            onClick={() => {
              onChange?.(tagPricing);
            }}
          >
            <Typography type="text-sm" className="text-link-100 underline">
              Apply Tag Pricing
            </Typography>
          </div>
        )
      }
      isAdditionalEditable={isEditable}
      additionalEditableType="text"
      onAdditionalChange={handleOnChange}
      canResetAdditional={coupon !== '-NA-'}
      onAdditionalReset={() => handleOnChange('-NA-')}
      amount={discountLineItem?.amount.toUnit() || 0}
      formatAmount={taxFormat}
    />
  );
};

const OrderDefaultTotals = ({ location }: { location: Location }) => {
  const defaultValue = convertToMoney(0, location.currency as Currency);
  return (
    <>
      <OrderLineItem name="Cart Total" amount={defaultValue.toUnit()} formatAmount={defaultValue.toFormat()} />
      <OrderLineItem
        name="Order Total"
        amount={defaultValue.toUnit()}
        formatAmount={defaultValue.toFormat()}
        weight="medium"
      />
    </>
  );
};

const OrderTax = ({
  isAmountEditable = true,
  isPercentageEditable = true,
  tax,
  isCycleBilling,
  setCustomTax,
  setCustomTaxPercentage,
  taxCodeOptions,
  isDelivery,
  taxBreakdown,
}: {
  isAmountEditable?: boolean;
  isPercentageEditable?: boolean;
  tax: TaxLineItem;
  isCycleBilling?: boolean;
  setCustomTax: (tax?: Dinero.Dinero) => void;
  setCustomTaxPercentage: (t?: number) => void;
  taxCodeOptions?: TaxCodeOptions;
  isDelivery: boolean;
  taxBreakdown?: TaxBreakdown[];
}) => {
  const { getMultipleConfigurations } = useAPI();
  const [useTaxJar, setUseTaxJar] = useState<boolean>(false);
  const [displayTaxByJurisdiction, setDisplayTaxByJurisdiction] = useState<boolean>();
  const previousDelivery = usePrevious(isDelivery);

  const init = async () => {
    const taxConfs = await getMultipleConfigurations(['CONF_TAXJAR', 'CONF_DISPLAY_JURISDICTION_TAXES_BREAKDOWN']);
    setUseTaxJar(
      taxConfs.find((t) => {
        return t.variable == 'CONF_TAXJAR';
      })?.value === '1',
    );
    setDisplayTaxByJurisdiction(
      taxConfs.find((t) => {
        return t.variable == 'CONF_DISPLAY_JURISDICTION_TAXES_BREAKDOWN';
      })?.value === '1',
    );
  };
  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    if (
      isDelivery &&
      useTaxJar &&
      taxCodeOptions &&
      (previousDelivery != isDelivery || taxCodeOptions.taxCode?.taxCodeSource === 'taxjar')
    ) {
      taxCodeOptions.setTaxCode({ id: 'taxJar', taxCodeSource: 'taxjar' });
    }
  }, [isDelivery]);

  const additionalChange = taxCodeOptions
    ? (taxRateOption: { label: string; value: string }) => {
        taxCodeOptions.setTaxCode({
          id: taxRateOption.value,
          name: taxRateOption.label,
          taxCodeSource: taxRateOption.label == 'TaxJar Calculation' ? 'taxjar' : undefined,
        });
        toast('Tax code updated.');
      }
    : (newPercentage: any) => {
        setCustomTaxPercentage(newPercentage);
        toast('Tax percentage updated.');
      };

  const additionalReset = taxCodeOptions
    ? () => {
        taxCodeOptions.setTaxCode({
          id: taxCodeOptions.defaultTaxCode || 'taxJar',
          taxCodeSource: !taxCodeOptions.defaultTaxCode ? 'taxjar' : undefined,
        });
        toast('Tax percentage updated to default.');
      }
    : () => {
        setCustomTaxPercentage(undefined);
        toast('Tax percentage updated to default.');
      };
  const options: { label: string; value: string }[] = [];
  if (useTaxJar) {
    options.push({ label: 'TaxJar Calculation', value: 'taxJar' });
  }
  const taxCodeOptionList =
    taxCodeOptions?.taxCodes?.map((tc) => {
      return { label: `${tc.name}`, value: tc.id };
    }) || [];

  let taxLabelValue = `(${tax.percentage}%)`;
  options.push(...taxCodeOptionList);
  let taxCode;
  if (tax.taxCodeId) {
    taxCode = taxCodeOptions?.taxCodes.find((_) => _.id == tax.taxCodeId && !_.name.startsWith('TaxJar'));
  }
  if (taxCode) {
    taxLabelValue = taxCode.name;
    if (!displayTaxByJurisdiction) {
      taxLabelValue = `${taxCode.name} (${taxCode.totalPercentage}%)`;
    }
  }
  return (
    <div>
      {isDelivery && taxCodeOptions && (
        <Typography type={'text-sm'} className={'text-red-500'}>
          Verify Tax Rate for Delivery
        </Typography>
      )}
      <OrderLineItem
        name={`${isCycleBilling ? '*' : ''}${tax.description}${
          !isPercentageEditable && !tax.hasCustomAmount ? ` ${taxLabelValue}` : ''
        }`}
        helperText={
          tax.userIsExempted && tax.hasCustomAmount && tax.hasCustomPercentage ? (
            <Tooltip className="flex cursor-help" type="black" tooltip="Customer is exempted from tax">
              <Icon icon="InfoCircle" stroke="var(--link-100)" />
            </Tooltip>
          ) : undefined
        }
        isAdditionalEditable={isPercentageEditable && !tax.hasCustomAmount}
        additionalLabel={taxCodeOptions ? 'Tax Code' : 'Percentage (%)'}
        additionalValue={taxCode ? taxCode.id : tax.percentage}
        additionalEditableType={taxCodeOptions ? 'dropdown' : 'number'}
        additionalFormattedValue={taxCode ? taxLabelValue : `(${tax.percentage}%)`}
        onAdditionalChange={additionalChange}
        canResetAdditional={tax.hasCustomPercentage}
        onAdditionalReset={additionalReset}
        amount={tax.amount.toUnit()}
        formatAmount={tax.amount.toFormat()}
        isEditable={taxCodeOptions ? false : isAmountEditable && !tax.hasCustomPercentage}
        options={options}
        onChange={
          taxCodeOptions
            ? () => {}
            : (amount) => {
                setCustomTax(Dinero({ amount: safeRound(amount * 100), currency: tax.amount.getCurrency() }));
                toast('Tax updated.');
              }
        }
        canReset={!taxCodeOptions && tax.hasCustomAmount}
        onReset={
          taxCodeOptions
            ? () => {}
            : () => {
                setCustomTax(undefined);
                toast('Tax updated to default.');
              }
        }
      />
      {displayTaxByJurisdiction &&
        taxBreakdown &&
        taxBreakdown.map((b) => {
          return (
            <div>
              <OrderLineItem
                name={`${b.jurisdiction} - (${b.percentage}%)`}
                amount={b.amount.toUnit()}
                formatAmount={b.amount.toFormat()}
                isEditable={false}
              />
            </div>
          );
        })}
    </div>
  );
};
